import { ValidationService,
         ClientValidationResult,
         PricingExcelApiResult,
         PromotionExpirationExcelApiValidationRequest,
         PromotionExpirationApiValidationRequestData,
         PromotionExpirationExcelApiValidationResponse,
         PromotionExpirationExcelDataRow,
         PromotionExpirationExcelTemplate,
         ExcelPropertyInfo,
         DateDetails,
         DateMetadata, 
         ExcelViewerErrorData } from "types";
import { ClientValidationError, ClientValidationErrorType } from "errors";
import { commonRegex, dateFormatter } from "utils";
import { PromotionExpirationExcelColumnHeaders, 
         PromotionExpirationTemplateErrorKeys,
         YesOrNo } from "enums";
import { useSecurityService, useDateCheckerService, useExcelValidationHelpers } from "services";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { ExcelTemplateTypes } from "enums"; 

export function usePromotionExpirationTemplateValidationService(id: string, fileName: string, changeNotes: string, postValidationCallback?: (args: any[]) => void): ValidationService {
    
    let excelViewerErrorData: ExcelViewerErrorData[] = [];

    const { getAuthHeader, getUserEmail } = useSecurityService();

    const { checkValidDay, 
            checkValidMonth, 
            checkValidYear,
            checkIsPastDate } = useDateCheckerService();

    const { checkRequiredField, 
            checkExcelHeaders,
            standardizePropertyNames,
            trimValuesLeadingAndTrailingWhitespace,
            tryParseInt: tryParseIntGeneric } = useExcelValidationHelpers();
    
    const tryParseInt = (prop: ExcelPropertyInfo) => tryParseIntGeneric(PromotionExpirationExcelColumnHeaders, prop);

    const apiUrl = process.env.REACT_APP_SALES_PRICINGIMPORT_PROMOTION_EXPIRATION_VALIDATION_API,
          maxPricingDecimalPlaces = 4,
          regex = { dateFormat: commonRegex.dateFormats.mmddyyyy,
                    ambiguousDateMonth: commonRegex.dateFormats.monthFirstAmbiguous,
                    aboveMaxPrecision: commonRegex.maxDecimalPrecision(maxPricingDecimalPlaces),
                    hasNonNumeric: commonRegex.hasNonNumeric,
                    allNines: commonRegex.allNines,
                    customerId: commonRegex.customerId,
                    agreementId: commonRegex.agreementId,
                    employeeResponsibleName: /^[A-Za-z][A-Za-z .'\-]*$/ };

    //Sends a POST request containing uploaded and client-side validated excel data to Sales.PricingImport
    const postToValidationApi = async (clientValidatedExcel: PromotionExpirationExcelDataRow[]) => {
        try {
            const outboundModel: PromotionExpirationExcelApiValidationRequest = await convertToRequestModel(clientValidatedExcel);
            const fileId = `${fileName}-${id}.json`;
            const templateType = ExcelTemplateTypes.PromotionExpiration;

            const config: AxiosRequestConfig = {
                headers: await getAuthHeader()
            };

            // Request a SAS Upload URL
            const sasResponse = await axios.post<{ BlobUrl: string }>(
                `${process.env.REACT_APP_SALES_PRICINGIMPORT_GENERATE_SAS_URL}`,
                { fileId, templateType },
                config
            );

            const sasUrl = sasResponse.data.BlobUrl;
            //console.log(`SAS URL for upload: ${sasUrl}`)

            // Upload JSON payload to Blob
            await axios.put(
                sasUrl,
                JSON.stringify(outboundModel),
                {
                    headers: {
                        "x-ms-blob-type": "BlockBlob",
                        "Content-Type": "application/json"
                    }
                }
            );

            const container = "pricing-template-submissions";
            const blobPath = `${templateType}/${fileId}`;

            // Notify backend (APIM) with blob reference
            const notifyBackendResponse = await axios.post(
                `${apiUrl}`,
                {
                    Container: container,
                    BlobPath: blobPath,
                    TemplateFileName: outboundModel.TemplateFileName,
                    SubmitterEmail: outboundModel.SubmitterEmail,
                    ChangeNotes: outboundModel.ChangeNotes,
                    CosmosGUID: outboundModel.CosmosGUID,
                },
                config
            );

            return {
                response: notifyBackendResponse.data as PromotionExpirationExcelApiValidationResponse,
                postError: "",
                statusCode: notifyBackendResponse.status
            } as PricingExcelApiResult;

        } catch (ex: any) {
            const error = ex as AxiosError;
            const status = error.response?.status ?? 0;

            return {
                response: error.response?.data as PromotionExpirationExcelApiValidationResponse,
                postError: error.message ?? "Error posting to validation API.",
                statusCode: status
            } as PricingExcelApiResult;
        }
    };   

    const doClientSideValidation = async (excel: PromotionExpirationExcelTemplate[] | null, skipNumHeaders: number) => {
        let errText: string = "";
        const errorList: ExcelViewerErrorData[] = [],
              panelErrors: string[] = [],
              validFlags = Object.values<string>(YesOrNo);

        excelViewerErrorData = [];
        const extraHeaderValidation = () => {
            //Checking for data in a few fields of the first data row to ensure that this isn't a blank template
            if (!excel) return;
            if (skipNumHeaders < 1) {
                if ( !( excel[0]["CUSTOMERID"] 
                     || excel[0]["CONTROLNUMBER"] 
                     || excel[0]["AGREEMENTNUMBER"] 
                     || excel[0]["PROMOTIONPERCENT"] 
                     || excel[0]["EXTEND/DELETE/CANCEL/NEW"]
                     || excel[0]["PROMOTIONID"] ) )
                {
                    throw new ClientValidationError(ClientValidationErrorType.NoData, 
                                                    "Excel file does not appear to contain any valid data rows.", 
                                                    null);
                }
            }
        };
        checkExcelHeaders(excel, skipNumHeaders, extraHeaderValidation);

        //Null suppression because the above call to checkExcelHeaders will
        //throw a ClientValidationError on null or empty excel data, so there's no need for another check.
        const excelData = convertToValidationModel(excel!, skipNumHeaders);

        //Loop through rows.  
        for (const [index, row] of excelData.entries()) {
            //1 to skip the header (keys) row, 1 to offset zero-indexing
            const rowNumber = index + 2 + skipNumHeaders;

            await doDateValidation(errorList, row, rowNumber);

            for (const prop of Object.getOwnPropertyNames(row)) {
                const thisCellValue = (`${row[prop as keyof PromotionExpirationExcelDataRow] ?? ""}`).trim(),
                      propNameKey = prop as keyof typeof PromotionExpirationExcelColumnHeaders,
                      propColumnName = `column "${PromotionExpirationExcelColumnHeaders[propNameKey]}"`,
                      errorKey = prop as keyof typeof PromotionExpirationTemplateErrorKeys,
                      thisPropInfo = { propName: propNameKey,
                                       value: thisCellValue,
                                       dataRow: rowNumber,
                                       errorKey: errorKey,
                                       log: errorList } as ExcelPropertyInfo;
                switch (prop) {
                    case "customerId":
                        if (!checkIfRequiredFieldIsNotEmpty(thisPropInfo)) {
                            break;
                        } else if (
                            thisCellValue &&
                            thisCellValue !== "*" &&
                            !/^\d+-\d+$/.test(thisCellValue)
                        ) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, must be either "*" or in format "digits-digits" (e.g., "123456-0").`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, propNameKey, [errText]));
                        }
                        break;
                    case "controlNumber":
                        if (thisCellValue && regex.hasNonNumeric.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, ` +
                                      `cannot contain non-numeric characters.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, propNameKey, [errText]));
                        }
                        break;
                    case "agreementNumber":
                        // Skip validation if blank (optional for promo expiration)
                        if (!thisCellValue || thisCellValue.trim() === "") {
                            break;
                        }

                        // Acceptable formats: "C-###" or a plain numeric string up to 9 digits
                        const cFormatRegex = /^C-\d{1,9}$/;
                        const plainNumberRegex = /^\d{1,9}$/;

                        if (thisCellValue && !(cFormatRegex.test(thisCellValue) || plainNumberRegex.test(thisCellValue))) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}. Must be in format "C-###" or a numeric string up to 9 digits.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, propNameKey, [errText]));
                        } else {
                            // Extract the numeric part (remove "C-" if present)
                            const numericPart = parseInt(thisCellValue.replace(/^C-/, ""), 10);
                            if (isNaN(numericPart) || numericPart < 0) {
                                errText = `Invalid value "${thisCellValue}" for ${propColumnName}, must be a non-negative number.`;
                                errorList.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys[errorKey], [errText]));
                            }
                        }
                        break;
                    case "action":
                        checkIfRequiredFieldIsNotEmpty(thisPropInfo);
                        break;
                    case "itemNumber":
                    case "productLine":
                    case "productLineCategory":
                        if (thisCellValue !== "" && thisCellValue !== "*" && parseInt(thisCellValue) < 0) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, cannot be a negative number.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys[errorKey], [errText]));
                        } else if (thisCellValue && regex.hasNonNumeric.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, ` +
                                      `cannot contain non-numeric characters.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, propNameKey, [errText]));
                        } 
                        break;
                    case "promotionAmount":
                    case "newPromotionAmount":
                    case "promotionPercent":
                        if (thisCellValue && regex.hasNonNumeric.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, ` +
                                      `cannot contain non-numeric characters.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, propNameKey, [errText]));
                        } else if (regex.aboveMaxPrecision.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, this field can have no more ` + 
                                      `than ${maxPricingDecimalPlaces} decimal places.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys[errorKey], [errText]));
                        }
                        break;
                    case "supplierFlag":
                        if (thisCellValue !== "" && validFlags.findIndex(x => x.toLowerCase() === thisCellValue.toLowerCase()) === -1) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, this field must contain ` + 
                                      `one of the following values: [${validFlags.join(", ")} or true, false]`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys[errorKey], [errText]));
                        }
                        break;
                    case "employee":
                        if (thisCellValue !== "" && !regex.employeeResponsibleName.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, must contain only letters, spaces, apostrophes, hyphens, or periods.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, propNameKey, [errText]));
                        }
                        break;
                    case "jobType":
                        const trimmedValue = String(thisCellValue).trim();

                        if (trimmedValue !== "" && !/^\d+$/.test(trimmedValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}. Must be blank or a non-negative whole number.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys[errorKey], [errText]));
                        }
                        break;
                }
            }
        }
        
        //When we're done looping through rows
        if (postValidationCallback) 
            postValidationCallback([...excelViewerErrorData, ...errorList]);

        errorList.forEach(x => panelErrors.push(`Data row ${x.row} - ${x.info.join(" | ")}`));
        
        return { requestData: excelData, 
                 errorList: panelErrors, 
                 errorCount: panelErrors.length } as ClientValidationResult;
    };
    
    //Strips spaces and lower case letters out of Excel template property names
    const standardizeExcelPropertyNameFormat = (rawExcel: any[]) => standardizePropertyNames<PromotionExpirationExcelTemplate>(rawExcel);

    //Logs error on missing required field 
    const checkIfRequiredFieldIsNotEmpty = (props: ExcelPropertyInfo) => checkRequiredField(PromotionExpirationExcelColumnHeaders, props);
        
    //Checks for invalid date formats, past dates, end dates before begin dates, and extend dates before end dates
    const doDateValidation = async (errors: ExcelViewerErrorData[], model: PromotionExpirationExcelDataRow, rowNumber: number) => {
        const format: RegExp = regex.dateFormat,
              ambiguousMatch: RegExp = regex.ambiguousDateMonth,
              beginOrderErrors: ExcelViewerErrorData[] = [],
              endOrderErrors: ExcelViewerErrorData[] = [],
              extendOrderErrors: ExcelViewerErrorData[] = [],
              newBeginningOrderErrors: ExcelViewerErrorData[] = [],
              newEndingOrderErrors: ExcelViewerErrorData[] = [],

              beginDeliveryErrors: ExcelViewerErrorData[] = [],
              endDeliveryErrors: ExcelViewerErrorData[] = [],
              extendDeliveryErrors: ExcelViewerErrorData[] = [],
              newBeginningDeliveryErrors: ExcelViewerErrorData[] = [],
              newEndingDeliveryErrors: ExcelViewerErrorData[] = [];

        let errText: string = "";

        const beginOrder = model.beginOrderDate?.toString().match(format),
              endOrder = model.endOrderDate?.toString().match(format),
              extendOrder = model.extendOrderDate?.toString().match(format),
              newBeginningOrder = model.newBeginningOrderDate?.toString().match(format),
              newEndingOrder = model.newEndingOrderDate?.toString().match(format),

              beginDelivery = model.beginDeliveryDate?.toString().match(format),
              endDelivery = model.endDeliveryDate?.toString().match(format),
              extendDelivery = model.extendDeliveryDate?.toString().match(format),
              newBeginningDelivery = model.newBeginningDeliveryDate?.toString().match(format),
              newEndingDelivery = model.newEndingDeliveryDate?.toString().match(format),

              bothOrderColumns = `${PromotionExpirationTemplateErrorKeys.beginOrderDate}, ` +
                                 `${PromotionExpirationTemplateErrorKeys.endOrderDate}`,
              bothDeliveryColumns = `${PromotionExpirationTemplateErrorKeys.beginDeliveryDate}, ` +
                                    `${PromotionExpirationTemplateErrorKeys.endDeliveryDate}`,
              extendEndOrderColumns = `${PromotionExpirationTemplateErrorKeys.extendOrderDate}, ` +
                                      `${PromotionExpirationTemplateErrorKeys.endOrderDate}`,
              extendEndDeliveryColumns = `${PromotionExpirationTemplateErrorKeys.extendDeliveryDate}, ` +
                                         `${PromotionExpirationTemplateErrorKeys.endDeliveryDate}`,
              bothNewOrderColumns = `${PromotionExpirationTemplateErrorKeys.newBeginningOrderDate}, ` +
                                    `${PromotionExpirationTemplateErrorKeys.newEndingOrderDate}`,
              bothNewDeliveryColumns = `${PromotionExpirationTemplateErrorKeys.newBeginningDeliveryDate}, ` +
                                       `${PromotionExpirationTemplateErrorKeys.newEndingDeliveryDate}`;

        const beginOrderDate = { year: parseInt(beginOrder?.groups?.year ?? ""),
                                 month: parseInt(beginOrder?.groups?.month ?? ""),
                                 day: parseInt(beginOrder?.groups?.day ?? "") } as DateDetails,

              endOrderDate = { year: parseInt(endOrder?.groups?.year ?? ""),
                               month: parseInt(endOrder?.groups?.month ?? ""),
                               day: parseInt(endOrder?.groups?.day ?? "") } as DateDetails,

              extendOrderDate = { year: parseInt(extendOrder?.groups?.year ?? ""),
                                  month: parseInt(extendOrder?.groups?.month ?? ""),
                                  day: parseInt(extendOrder?.groups?.day ?? "") } as DateDetails,

              newBeginningOrderDate = { year: parseInt(newBeginningOrder?.groups?.year ?? ""),
                                    month: parseInt(newBeginningOrder?.groups?.month ?? ""),
                                    day: parseInt(newBeginningOrder?.groups?.day ?? "") } as DateDetails,

              newEndingOrderDate = { year: parseInt(newEndingOrder?.groups?.year ?? ""),
                                    month: parseInt(newEndingOrder?.groups?.month ?? ""),
                                    day: parseInt(newEndingOrder?.groups?.day ?? "") } as DateDetails,

              beginDeliveryDate = { year: parseInt(beginDelivery?.groups?.year ?? ""),
                                    month: parseInt(beginDelivery?.groups?.month ?? ""),
                                    day: parseInt(beginDelivery?.groups?.day ?? "") } as DateDetails,

              endDeliveryDate = { year: parseInt(endDelivery?.groups?.year ?? ""),
                                  month: parseInt(endDelivery?.groups?.month ?? ""),
                                  day: parseInt(endDelivery?.groups?.day ?? "") } as DateDetails,

              extendDeliveryDate = { year: parseInt(extendDelivery?.groups?.year ?? ""),
                                     month: parseInt(extendDelivery?.groups?.month ?? ""),
                                     day: parseInt(extendDelivery?.groups?.day ?? "") } as DateDetails,

              newBeginningDeliveryDate = { year: parseInt(newBeginningDelivery?.groups?.year ?? ""),
                                       month: parseInt(newBeginningDelivery?.groups?.month ?? ""),
                                       day: parseInt(newBeginningDelivery?.groups?.day ?? "") } as DateDetails,

              newEndingDeliveryDate = { year: parseInt(newEndingDelivery?.groups?.year ?? ""),
                                     month: parseInt(newEndingDelivery?.groups?.month ?? ""),
                                     day: parseInt(newEndingDelivery?.groups?.day ?? "") } as DateDetails,

              beginOrderMetadata = { fullDateString: model.beginOrderDate,
                                     date: beginOrderDate,
                                     dateType: PromotionExpirationTemplateErrorKeys.beginOrderDate,
                                     dateColumnName: PromotionExpirationTemplateErrorKeys.beginOrderDate,
                                     rowNumber: rowNumber } as DateMetadata,
                                     
              endOrderMetadata = { fullDateString: model.endOrderDate,
                                   date: endOrderDate,
                                   dateType: PromotionExpirationTemplateErrorKeys.endOrderDate,
                                   dateColumnName: PromotionExpirationTemplateErrorKeys.endOrderDate,
                                   rowNumber: rowNumber } as DateMetadata,
                                     
              extendOrderMetadata = { fullDateString: model.extendOrderDate,
                                      date: extendOrderDate,
                                      dateType: PromotionExpirationTemplateErrorKeys.extendOrderDate,
                                      dateColumnName: PromotionExpirationTemplateErrorKeys.extendOrderDate,
                                      rowNumber: rowNumber } as DateMetadata,

              newBeginningOrderMetadata = { fullDateString: model.newBeginningOrderDate,
                                            date: newBeginningOrderDate,
                                            dateType: PromotionExpirationTemplateErrorKeys.newBeginningOrderDate,
                                            dateColumnName: PromotionExpirationTemplateErrorKeys.newBeginningOrderDate,
                                            rowNumber: rowNumber } as DateMetadata,

              newEndingOrderMetadata = { fullDateString: model.newEndingOrderDate,
                                         date: newEndingOrderDate,
                                         dateType: PromotionExpirationTemplateErrorKeys.newEndingOrderDate,
                                         dateColumnName: PromotionExpirationTemplateErrorKeys.newEndingOrderDate,
                                         rowNumber: rowNumber } as DateMetadata,
                                      
              beginDeliveryMetadata = { fullDateString: model.beginDeliveryDate,
                                        date: beginDeliveryDate,
                                        dateType: PromotionExpirationTemplateErrorKeys.beginDeliveryDate,
                                        dateColumnName: PromotionExpirationTemplateErrorKeys.beginDeliveryDate,
                                        rowNumber: rowNumber } as DateMetadata,
                
              endDeliveryMetadata = { fullDateString: model.endDeliveryDate,
                                      date: endDeliveryDate,
                                      dateType: PromotionExpirationTemplateErrorKeys.endDeliveryDate,
                                      dateColumnName: PromotionExpirationTemplateErrorKeys.endDeliveryDate,
                                      rowNumber: rowNumber } as DateMetadata,

              extendDeliveryMetadata = { fullDateString: model.extendDeliveryDate,
                                         date: extendDeliveryDate,
                                         dateType: PromotionExpirationTemplateErrorKeys.extendDeliveryDate,
                                         dateColumnName: PromotionExpirationTemplateErrorKeys.extendDeliveryDate,
                                         rowNumber: rowNumber } as DateMetadata,

              newBeginningDeliveryMetadata = { fullDateString: model.newBeginningDeliveryDate,
                                               date: newBeginningDeliveryDate,
                                               dateType: PromotionExpirationTemplateErrorKeys.newBeginningDeliveryDate,
                                               dateColumnName: PromotionExpirationTemplateErrorKeys.newBeginningDeliveryDate,
                                               rowNumber: rowNumber } as DateMetadata,

              newEndingDeliveryMetadata = { fullDateString: model.newEndingDeliveryDate,
                                            date: newEndingDeliveryDate,
                                            dateType: PromotionExpirationTemplateErrorKeys.newEndingDeliveryDate,
                                            dateColumnName: PromotionExpirationTemplateErrorKeys.newEndingDeliveryDate,
                                            rowNumber: rowNumber } as DateMetadata;

        const flags = { sameYear: beginOrderDate.year === endOrderDate.year,
                        sameMonth: (beginOrderDate.year === endOrderDate.year && beginOrderDate.month === endOrderDate.month),
                        invalidMonth: false,
                        invalidYear: false,
                        invalidDay: false,
                        badFormat: false,

                        badBeginOrderDate: false,
                        badEndOrderDate: false,
                        badExtendOrderDate: false,
                        badNewBeginningOrderDate: false,
                        badNewEndingOrderDate: false,

                        badBeginDeliveryDate: false,
                        badEndDeliveryDate: false,
                        badExtendDeliveryDate: false,
                        badNewBeginningDeliveryDate: false,
                        badNewEndingDeliveryDate: false,

                        allNinesEndOrderDate: false,
                        allNinesEndDeliveryDate: false,
                        allNinesExtendOrderDate: false,
                        allNinesExtendDeliveryDate: false, 
                        allNinesNewEndingOrderDate: false,
                        allNinesNewEndingDeliveryDate: false };

        //beginOrderDate checks
        if (model.beginOrderDate) {
            flags.invalidYear = !checkValidYear(beginOrderMetadata, beginOrderErrors);
            flags.invalidMonth = !checkValidMonth(beginOrderMetadata, beginOrderErrors);
            flags.invalidDay = !checkValidDay(beginOrderMetadata, beginOrderErrors, flags.invalidMonth, flags.invalidYear);

            const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

            flags.badFormat = badDateFormat;
            flags.badBeginOrderDate = badDateFormat;
        }

        //endOrderDate checks
        if (model.endOrderDate) {
            flags.allNinesEndOrderDate = regex.allNines.test(model.endOrderDate);
            if (flags.allNinesEndOrderDate) {
                flags.badEndOrderDate = true;
                errText = `Invalid end date of 99999999. Please use 12319999 instead.`
                errors.push(new ExcelViewerErrorData(rowNumber, bothOrderColumns, [errText]));
            }

            if (!flags.allNinesEndOrderDate) {
                //checkIsPastDate(endOrderMetadata, endOrderErrors);
                flags.invalidYear = !checkValidYear(endOrderMetadata, endOrderErrors);
                flags.invalidMonth = !checkValidMonth(endOrderMetadata, endOrderErrors);
                flags.invalidDay = !checkValidDay(endOrderMetadata, endOrderErrors, flags.invalidMonth, flags.invalidYear);
                
                const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

                flags.badEndOrderDate = badDateFormat;
                flags.badFormat = flags.badFormat || badDateFormat;
                
                //Date range checks
                if( (flags.sameYear && beginOrderDate.month > endOrderDate.month)
                    || (flags.sameMonth && beginOrderDate.day > endOrderDate.day)
                    || (endOrderDate.year < beginOrderDate.year) ) {
                    errText = `Invalid date range of "${model.beginOrderDate}" - "${model.endOrderDate}", ` + 
                              `reason: end order date is before begin order date.`
                    errors.push(new ExcelViewerErrorData(rowNumber, bothOrderColumns, [errText]));
                }
            }
        }

        //newBeginningOrderDate checks
        if (model.newBeginningOrderDate) {
            flags.invalidYear = !checkValidYear(newBeginningOrderMetadata, newBeginningOrderErrors);
            flags.invalidMonth = !checkValidMonth(newBeginningOrderMetadata, newBeginningOrderErrors);
            flags.invalidDay = !checkValidDay(newBeginningOrderMetadata, newBeginningOrderErrors, flags.invalidMonth, flags.invalidYear);

            const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

            flags.badFormat = badDateFormat;
            flags.badNewBeginningOrderDate = badDateFormat;
        }

        //newEndingOrderDate checks
        if (model.newEndingOrderDate) {
            flags.allNinesNewEndingOrderDate = regex.allNines.test(model.newEndingOrderDate);
            if (flags.allNinesNewEndingOrderDate) {
                flags.badNewEndingOrderDate = true;
                errText = `Invalid end date of 99999999. Please use 12319999 instead.`
                errors.push(new ExcelViewerErrorData(rowNumber, bothOrderColumns, [errText]));
            }

            if (!flags.allNinesEndOrderDate) {
                flags.invalidYear = !checkValidYear(newEndingOrderMetadata, newEndingOrderErrors);
                flags.invalidMonth = !checkValidMonth(newEndingOrderMetadata, newEndingOrderErrors);
                flags.invalidDay = !checkValidDay(newEndingOrderMetadata, newEndingOrderErrors, flags.invalidMonth, flags.invalidYear);

                const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

                flags.badFormat = badDateFormat;
                flags.badNewEndingOrderDate = badDateFormat;

                //Date range checks
                if ((flags.sameYear && newBeginningOrderDate.month > newEndingOrderDate.month)
                    || (flags.sameMonth && newBeginningOrderDate.day > newEndingOrderDate.day)
                    || (newEndingOrderDate.year < newBeginningOrderDate.year)) {
                    errText = `Invalid date range of "${model.newBeginningOrderDate}" - "${model.newEndingOrderDate}", ` +
                        `reason: new end order date is before new begin order date.`
                    errors.push(new ExcelViewerErrorData(rowNumber, bothNewOrderColumns, [errText]));
                }
            }
        }

        //beginDeliveryDate checks
        if (model.beginDeliveryDate) {
            flags.invalidYear = !checkValidYear(beginDeliveryMetadata, beginDeliveryErrors);
            flags.invalidMonth = !checkValidMonth(beginDeliveryMetadata, beginDeliveryErrors);
            flags.invalidDay = !checkValidDay(beginDeliveryMetadata, beginDeliveryErrors, flags.invalidMonth, flags.invalidYear);

            const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

            flags.badFormat = badDateFormat;
            flags.badBeginDeliveryDate = badDateFormat;
        }

        //endDeliveryDate checks
        if (model.endDeliveryDate) {
            flags.allNinesEndDeliveryDate = regex.allNines.test(model.endDeliveryDate);
            if (flags.allNinesEndDeliveryDate) {
                flags.badEndDeliveryDate = true;
                errText = `Invalid end date of 99999999. Please use 12319999 instead.`
                errors.push(new ExcelViewerErrorData(rowNumber, bothOrderColumns, [errText]));
            }

            if (!flags.allNinesEndDeliveryDate) {
                //checkIsPastDate(endDeliveryMetadata, endDeliveryErrors);                
                flags.sameYear = beginDeliveryDate.year === endDeliveryDate.year;
                flags.sameMonth = (beginDeliveryDate.year === endDeliveryDate.year && beginDeliveryDate.month === endDeliveryDate.month);
                flags.invalidYear = !checkValidYear(endDeliveryMetadata, endDeliveryErrors);
                flags.invalidMonth = !checkValidMonth(endDeliveryMetadata, endDeliveryErrors);
                flags.invalidDay = !checkValidDay(endDeliveryMetadata, endDeliveryErrors, flags.invalidMonth, flags.invalidYear);
                
                const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

                flags.badEndDeliveryDate = badDateFormat;
                flags.badFormat = flags.badFormat || badDateFormat;

                //Date range checks
                if( (flags.sameYear && beginDeliveryDate.month > endDeliveryDate.month)
                    || (flags.sameMonth && beginDeliveryDate.day > endDeliveryDate.day)
                    || (endDeliveryDate.year < beginDeliveryDate.year) ) {
                    errText = `Invalid date range of "${model.beginDeliveryDate}" - "${model.endDeliveryDate}", ` + 
                              `reason: end delivery date is before begin delivery date.`
                    errors.push(new ExcelViewerErrorData(rowNumber, bothDeliveryColumns, [errText]));
                }
            }
        }

        //newBeginningDeliveryDate checks
        if (model.newBeginningDeliveryDate) {
            flags.invalidYear = !checkValidYear(newBeginningDeliveryMetadata, newBeginningDeliveryErrors);
            flags.invalidMonth = !checkValidMonth(newBeginningDeliveryMetadata, newBeginningDeliveryErrors);
            flags.invalidDay = !checkValidDay(newBeginningDeliveryMetadata, newBeginningDeliveryErrors, flags.invalidMonth, flags.invalidYear);

            const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

            flags.badFormat = badDateFormat;
            flags.badNewBeginningDeliveryDate = badDateFormat;
        }

        //newEndingDeliveryDate checks
        if (model.newEndingDeliveryDate) {
            flags.allNinesNewEndingDeliveryDate = regex.allNines.test(model.newEndingDeliveryDate);
            if (flags.allNinesNewEndingDeliveryDate) {
                flags.badNewEndingDeliveryDate = true;
                errText = `Invalid end date of 99999999. Please use 12319999 instead.`
                errors.push(new ExcelViewerErrorData(rowNumber, bothOrderColumns, [errText]));
            }

            if (!flags.allNinesNewEndingDeliveryDate) {
                flags.invalidYear = !checkValidYear(newEndingDeliveryMetadata, newEndingDeliveryErrors);
                flags.invalidMonth = !checkValidMonth(newEndingDeliveryMetadata, newEndingDeliveryErrors);
                flags.invalidDay = !checkValidDay(newEndingDeliveryMetadata, newEndingDeliveryErrors, flags.invalidMonth, flags.invalidYear);

                const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

                flags.badFormat = badDateFormat;
                flags.badNewEndingDeliveryDate = badDateFormat;

                //Date range checks
                if ((flags.sameYear && newBeginningDeliveryDate.month > newEndingDeliveryDate.month)
                    || (flags.sameMonth && newBeginningDeliveryDate.day > newEndingDeliveryDate.day)
                    || (newEndingDeliveryDate.year < newBeginningDeliveryDate.year)) {
                    errText = `Invalid date range of "${model.newBeginningDeliveryDate}" - "${model.newEndingDeliveryDate}", ` +
                        `reason: new end delivery date is before new begin delivery date.`
                    errors.push(new ExcelViewerErrorData(rowNumber, bothNewDeliveryColumns, [errText]));
                }
            }
        }

        //extendOrderDate checks
        if (model.extendOrderDate) {
            flags.allNinesExtendOrderDate = regex.allNines.test(model.extendOrderDate);
            //Check if we even can extend
            if (flags.allNinesEndOrderDate) {
                errText = `Cannot extend promotion that does not expire`;
                errors.push(new ExcelViewerErrorData(rowNumber, extendEndOrderColumns, [errText]));
            }
            else if (!flags.allNinesExtendOrderDate) {
                checkIsPastDate(extendOrderMetadata, extendOrderErrors);
                flags.sameYear = extendOrderDate.year === endOrderDate.year;
                flags.sameMonth = extendOrderDate.year === endOrderDate.year && extendOrderDate.month === endOrderDate.month;
                flags.invalidYear = !checkValidYear(extendOrderMetadata, extendOrderErrors);
                flags.invalidMonth = !checkValidMonth(extendOrderMetadata, extendOrderErrors);
                flags.invalidDay = !checkValidDay(extendOrderMetadata, extendOrderErrors, flags.invalidMonth, flags.invalidYear);

                const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

                flags.badExtendOrderDate = badDateFormat;
                flags.badFormat = flags.badFormat || badDateFormat;
                
                //Date range checks
                if( (flags.sameYear && endOrderDate.month > extendOrderDate.month)
                    || (flags.sameMonth && endOrderDate.day > extendOrderDate.day)
                    || (extendOrderDate.year < endOrderDate.year) ) {
                    errText = `Invalid extend order date of "${model.extendOrderDate}", ` + 
                              `reason: extend order date must be after end order date of "${model.endOrderDate}".`
                    errors.push(new ExcelViewerErrorData(rowNumber, extendEndOrderColumns, [errText]));
                }
            }
        }

        //extendDeliveryDate checks
        if (model.extendDeliveryDate) {
            flags.allNinesExtendDeliveryDate = regex.allNines.test(model.extendDeliveryDate);
            //Check if we even can extend
            if (flags.allNinesEndDeliveryDate) {
                errText = `Cannot extend promotion that does not expire`;
                errors.push(new ExcelViewerErrorData(rowNumber, extendEndDeliveryColumns, [errText]));
            }
            else if (!flags.allNinesExtendDeliveryDate) {
                checkIsPastDate(extendDeliveryMetadata, extendDeliveryErrors);
                flags.sameYear = extendDeliveryDate.year === endDeliveryDate.year;
                flags.sameMonth = extendDeliveryDate.year === endDeliveryDate.year && extendDeliveryDate.month === endDeliveryDate.month;
                flags.invalidYear = !checkValidYear(extendDeliveryMetadata, extendDeliveryErrors);
                flags.invalidMonth = !checkValidMonth(extendDeliveryMetadata, extendDeliveryErrors);
                flags.invalidDay = !checkValidDay(extendDeliveryMetadata, extendDeliveryErrors, flags.invalidMonth, flags.invalidYear);

                const badDateFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;

                flags.badExtendDeliveryDate = badDateFormat;
                flags.badFormat = flags.badFormat || badDateFormat;
                
                //Date range checks
                if( (flags.sameYear && endDeliveryDate.month > extendDeliveryDate.month)
                    || (flags.sameMonth && endDeliveryDate.day > extendDeliveryDate.day)
                    || (extendDeliveryDate.year < endDeliveryDate.year) ) {
                    errText = `Invalid extend delivery date of "${model.extendDeliveryDate}", ` + 
                              `reason: extend delivery date must be after end delivery date of "${model.endDeliveryDate}".`
                    errors.push(new ExcelViewerErrorData(rowNumber, extendEndDeliveryColumns, [errText]));
                }
            }
        }
        
        //Ambiguity checks
        const hasAmbiguousBeginOrderDate = ambiguousMatch.test(model.beginOrderDate?.toString() ?? ""),
              hasAmbiguousEndOrderDate = !flags.allNinesEndOrderDate && ambiguousMatch.test(model.endOrderDate?.toString() ?? ""),
              hasAmbiguousExtendOrderDate = !flags.allNinesExtendOrderDate && ambiguousMatch.test(model.extendOrderDate?.toString() ?? ""),
              hasAmbiguousNewBeginOrderDate = ambiguousMatch.test(model.newBeginningOrderDate?.toString() ?? ""),
              hasAmbiguousNewEndOrderDate = !flags.allNinesNewEndingOrderDate && ambiguousMatch.test(model.newEndingOrderDate?.toString() ?? ""),
              hasAmbiguousBeginDeliveryDate = ambiguousMatch.test(model.beginDeliveryDate?.toString() ?? ""),
              hasAmbiguousEndDeliveryDate = !flags.allNinesEndDeliveryDate && ambiguousMatch.test(model.endDeliveryDate?.toString() ?? ""),
              hasAmbiguousExtendDeliveryDate = !flags.allNinesExtendDeliveryDate && ambiguousMatch.test(model.extendDeliveryDate?.toString() ?? ""),
              hasAmbiguousNewBeginDeliveryDate = ambiguousMatch.test(model.newBeginningDeliveryDate?.toString() ?? ""),
              hasAmbiguousNewEndDeliveryDate = !flags.allNinesNewEndingDeliveryDate && ambiguousMatch.test(model.newEndingDeliveryDate?.toString() ?? "");

        const ambiguityErrorTextResolver = (propertyName: string) => {
            return `${propertyName} is ambiguous - add forward-slash ('/') separators between month, day, and year, ` + 
                   "or use the full 8-digit MMDDYYYY format with leading zeroes for month and/or day where appropriate."
        };

        //Order date ambiguity
        if (hasAmbiguousBeginOrderDate) {
            flags.badBeginOrderDate = true;
            errText = ambiguityErrorTextResolver("Begin order date");
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.beginOrderDate, [errText]));
        }
        if (hasAmbiguousEndOrderDate) {
            flags.badEndOrderDate = true;
            errText = ambiguityErrorTextResolver("End order date");
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.endOrderDate, [errText]));
        }
        if (hasAmbiguousExtendOrderDate) {
            flags.badExtendOrderDate = true;
            errText = ambiguityErrorTextResolver("Extend order date");
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.extendOrderDate, [errText]));
        }
        if (hasAmbiguousNewBeginOrderDate) {
            flags.badNewBeginningOrderDate = true;
            errText = ambiguityErrorTextResolver("New beginning order date");
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.newBeginningOrderDate, [errText]));
        }
        if (hasAmbiguousNewEndOrderDate) {
            flags.badNewEndingOrderDate = true;
            errText = ambiguityErrorTextResolver("New ending order date");
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.newEndingOrderDate, [errText]));
        }
        
        //Delivery date ambiguity
        if (hasAmbiguousBeginDeliveryDate) {
            flags.badBeginDeliveryDate = true;
            errText = ambiguityErrorTextResolver("Begin delivery date");
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.beginDeliveryDate, [errText]));
        }
        if (hasAmbiguousEndDeliveryDate) {
            flags.badEndDeliveryDate = true;
            errText = ambiguityErrorTextResolver("End delivery date");
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.endDeliveryDate, [errText]));
        }
        if (hasAmbiguousExtendDeliveryDate) {
            flags.badExtendDeliveryDate = true;
            errText = ambiguityErrorTextResolver("Extend delivery date");
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.extendDeliveryDate, [errText]));
        }
        if (hasAmbiguousNewBeginDeliveryDate) {
            flags.badNewBeginningDeliveryDate = true;
            errText = ambiguityErrorTextResolver("New beginning delivery date");
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.newBeginningDeliveryDate, [errText]));
        }
        if (hasAmbiguousNewEndDeliveryDate) {
            flags.badNewEndingDeliveryDate = true;
            errText = ambiguityErrorTextResolver("New ending delivery date");
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.newEndingDeliveryDate, [errText]));
        }

        //Aggregation
        if (flags.badBeginOrderDate) {
            errText = "Begin order date appears not to be in correct format.";
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.beginOrderDate, [errText]));
        } else {
            //These errors are only valuable if begin date format is correct
            errors.push(...beginOrderErrors);
        }

        if (flags.badEndOrderDate) {
            errText = 'End order date appears not to be in correct format.';
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.endOrderDate, [errText]));
        } else {
            //These errors are only valuable if end date format is correct
            errors.push(...endOrderErrors);
        }
        
        if (flags.badExtendOrderDate) {
            errText = 'Extend order date appears not to be in correct format.';
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.extendOrderDate, [errText]));
        } else {
            //These errors are only valuable if extend date format is correct
            errors.push(...extendOrderErrors);
        }

        if (flags.badNewBeginningOrderDate) {
            errText = "New begin order date appears not to be in correct format.";
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.newBeginningOrderDate, [errText]));
        } else {
            //These errors are only valuable if begin date format is correct
            errors.push(...newBeginningOrderErrors);
        }

        if (flags.badNewEndingOrderDate) {
            errText = 'New end order date appears not to be in correct format.';
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.newEndingOrderDate, [errText]));
        } else {
            //These errors are only valuable if end date format is correct
            errors.push(...newEndingOrderErrors);
        }

        if (flags.badBeginDeliveryDate) {
            errText = "Begin delivery date appears not to be in correct format.";
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.beginDeliveryDate, [errText]));
        } else {
            //These errors are only valuable if begin date format is correct
            errors.push(...beginDeliveryErrors);
        }

        if (flags.badEndDeliveryDate) {
            errText = 'End delivery date appears not to be in correct format.';
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.endDeliveryDate, [errText]));
        } else {
            //These errors are only valuable if end date format is correct
            errors.push(...endDeliveryErrors);
        }
        
        if (flags.badExtendDeliveryDate) {
            errText = 'Extend delivery date appears not to be in correct format.';
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.extendDeliveryDate, [errText]));
        } else {
            //These errors are only valuable if extend date format is correct
            errors.push(...extendDeliveryErrors);
        }

        if (flags.badNewBeginningDeliveryDate) {
            errText = "New begin delivery date appears not to be in correct format.";
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.newBeginningDeliveryDate, [errText]));
        } else {
            //These errors are only valuable if begin date format is correct
            errors.push(...newBeginningDeliveryErrors);
        }

        if (flags.badNewEndingDeliveryDate) {
            errText = 'New end delivery date appears not to be in correct format.';
            errors.push(new ExcelViewerErrorData(rowNumber, PromotionExpirationTemplateErrorKeys.newEndingDeliveryDate, [errText]));
        } else {
            //These errors are only valuable if end date format is correct
            errors.push(...newEndingDeliveryErrors);
        }

        if (flags.badFormat) {
            errText = `Ensure that all dates are in the correct format: MMDDYYYY`;
            errors.push(new ExcelViewerErrorData(rowNumber, "", [errText]));
        }

        //Because we're accepting MMDDYYYY values for months 1-9 even without a leading zero, 
        //we need to standardize the format for the validation API
        if (model.beginOrderDate) 
            model.beginOrderDate = dateFormatter.MMDDYYYY(beginOrderDate);

        if (model.endOrderDate && !flags.allNinesEndOrderDate) 
            model.endOrderDate = dateFormatter.MMDDYYYY(endOrderDate);

        if (model.extendOrderDate && !flags.allNinesExtendOrderDate) 
            model.extendOrderDate = dateFormatter.MMDDYYYY(extendOrderDate);

        if (model.newBeginningOrderDate)
            model.newBeginningOrderDate = dateFormatter.MMDDYYYY(newBeginningOrderDate);

        if (model.newEndingOrderDate && !flags.allNinesNewEndingOrderDate)
            model.newEndingOrderDate = dateFormatter.MMDDYYYY(newEndingOrderDate);

        if (model.beginDeliveryDate) 
            model.beginDeliveryDate = dateFormatter.MMDDYYYY(beginDeliveryDate);

        if (model.endDeliveryDate && !flags.allNinesEndDeliveryDate) 
            model.endDeliveryDate = dateFormatter.MMDDYYYY(endDeliveryDate);

        if (model.extendDeliveryDate && !flags.allNinesExtendDeliveryDate) 
            model.extendDeliveryDate = dateFormatter.MMDDYYYY(extendDeliveryDate);

        if (model.newBeginningDeliveryDate)
            model.newBeginningDeliveryDate = dateFormatter.MMDDYYYY(newBeginningDeliveryDate);

        if (model.newEndingDeliveryDate && !flags.allNinesNewEndingDeliveryDate)
            model.newEndingDeliveryDate = dateFormatter.MMDDYYYY(newEndingDeliveryDate);
    };
    
    //Model conversions
    const convertToValidationModel = (rawExcelTemplate: PromotionExpirationExcelTemplate[], skipNumHeaders: number): PromotionExpirationExcelDataRow[] => {
        let validationModel: PromotionExpirationExcelDataRow[] = [];
        const excelTemplate = trimValuesLeadingAndTrailingWhitespace<PromotionExpirationExcelTemplate>(rawExcelTemplate);

        for (let i = skipNumHeaders; i < excelTemplate.length; i++) {
            const fieldIsEmpty = (prop: ExcelPropertyInfo) => (prop.value?.toString().trim().length ?? 0) === 0,
                  tryParseIfNotNull = (prop: ExcelPropertyInfo) => (fieldIsEmpty(prop) ? null : tryParseInt(prop)),
                  rowData = {} as PromotionExpirationExcelDataRow,
                  excelData = excelTemplate[i],
                  propInfo = { propName: "",
                               value: null,
                               dataRow: i + 2,
                               log: [] } as ExcelPropertyInfo;

            let customerNumber, 
                customerShipToNumber;

            for (const property of Object.getOwnPropertyNames(excelData)) {
                const key = property as keyof typeof excelData;
                if (excelData[key] && typeof(excelData[key] === 'string')) {
                    excelData[key] = (`${excelData[key]}`).trim();
                }
            }

            //Non-integer fields
            const customerId = excelData.CUSTOMERID ?? "";
            if (customerId && customerId !== "*") 
            {
                const parsedCustomer = regex.customerId.exec(customerId)?.groups;
                if (parsedCustomer) {
                    customerNumber = parsedCustomer["customer"];
                    customerShipToNumber = parsedCustomer["shipto"];
                }
            }

            rowData.agreementNumber = excelData.AGREEMENTNUMBER ?? "";
            rowData.controlNumber = excelData.CONTROLNUMBER ?? "";
            rowData.customerAddress = excelData.CUSTOMERID ?? "";
            rowData.customerName = excelData.CUSTOMERNAME ?? "";
            rowData.customerAddress = excelData.CUSTOMERADDRESS ?? "";
            rowData.customerId = customerId;

            rowData.beginOrderDate = excelData.BEGINORDERDATE ?? "";
            rowData.endOrderDate = excelData.ENDORDERDATE ?? "";
            rowData.extendOrderDate = excelData.EXTENDORDERENDDATE ?? "";

            rowData.beginDeliveryDate = excelData.BEGINDELIVERYDATE ?? "";
            rowData.endDeliveryDate = excelData.ENDDELIVERYDATE ?? "";
            rowData.extendDeliveryDate = excelData.EXTENDDELIVERYENDDATE ?? "";
            
            rowData.segmentDescription = excelData.SEGMENTDESC ?? "";
            rowData.tierDescription = excelData.TIERDESC ?? "";
            rowData.channelDescription = excelData.CHANNELDESC ?? "";
            rowData.priceZoneDescription = excelData.PRICEZONEDESC ?? "";
            rowData.buyingGroupDescription = excelData.BUYINGGROUPDESC ?? "";
            rowData.corporateAffiliationDescription = excelData.CORPORATEAFFILIATIONDESC ?? "";

            rowData.promotionIdDescription = excelData.PROMOTIONIDDESC ?? "";
            rowData.promotionId = excelData.PROMOTIONID ?? "";

            rowData.itemDescription = excelData.ITEMDESC ?? "";
            rowData.productLineDescription = excelData.PRODUCTLINEDESC ?? "";
            rowData.productLineCategoryDescription = excelData.PRODUCTCATEGORYDESC ?? "";
            rowData.manufacturerNumber = excelData.MANUFACTURERSNUMBER ?? "";
            rowData.gtin = excelData.GTIN ?? "";
            rowData.uom = excelData.UOM ?? "";

            rowData.minimumValue = excelData.MINIMUMVALUE ?? "";
            rowData.minimumDescription = excelData.MINIMUMDESC ?? "";

            rowData.maximumValue = excelData.MAXIMUMVALUE ?? "";
            rowData.maximumDescription = excelData.MAXIMUMDESC ?? "";

            rowData.contactEmail = excelData.CONTACTEMAIL ?? "";
            rowData.action = excelData["EXTEND/DELETE/CANCEL/NEW"] ?? "";

            let rawFlag = excelData.SUPPLIERFLAG?.toString().trim().toLowerCase();
            if (rawFlag === "true") {
                rowData.supplierFlag = "Y";
            } else if (rawFlag === "false") {
                rowData.supplierFlag = "N";
            } else {
                rowData.supplierFlag = excelData.SUPPLIERFLAG ?? "";
            }

            rowData.jobTypeDescription = excelData.JOBTYPEDESC ?? "";

            rowData.countryCode = excelData.COUNTRYCODE ?? "";
            rowData.countryDescription = excelData.COUNTRYDESC ?? "";
            rowData.freightZone = excelData.FREIGHTZONE ?? "";

            rowData.newPromotionUOM = excelData.NEWPROMOTIONUOM ?? "";
            rowData.newBeginningOrderDate = excelData.NEWBEGINNINGORDERDATE ?? "";
            rowData.newEndingOrderDate = excelData.NEWENDINGORDERDATE ?? "";
            rowData.newBeginningDeliveryDate = excelData.NEWBEGINNINGDELIVERYDATE ?? "";
            rowData.newEndingDeliveryDate = excelData.NEWENDINGDELIVERYDATE ?? "";

            rowData.comments = excelData.COMMENTS ?? "";

            //Defaulting these to zero seemed like maybe a bad idea
            rowData.promotionAmount = excelData.PROMOTIONAMOUNT;
            rowData.promotionPercent = excelData.PROMOTIONPERCENT;
            rowData.newPromotionAmount = excelData.NEWPROMOTIONAMOUNT;

            //Integer fields

            //Item number
            propInfo.propName = PromotionExpirationTemplateErrorKeys.itemNumber;
            propInfo.value = excelData.ITEMNUMBER;
            if (propInfo.value && propInfo.value !== "*") {
                rowData.itemNumber = tryParseIfNotNull(propInfo);
            }

            //Product line number
            propInfo.propName = PromotionExpirationTemplateErrorKeys.productLine;
            propInfo.value = excelData.PRODUCTLINE;
            if (propInfo.value && propInfo.value !== "*") {
                rowData.productLine = tryParseIfNotNull(propInfo);
            }

            //Product category number
            propInfo.propName = PromotionExpirationTemplateErrorKeys.productLineCategory;
            propInfo.value = excelData.PRODUCTCATEGORY;
            if (propInfo.value && propInfo.value !== "*") {
                rowData.productLineCategory = tryParseIfNotNull(propInfo);
            }

            //Job type
            propInfo.propName = PromotionExpirationTemplateErrorKeys.jobType;
            propInfo.value = excelData.JOBTYPE;
            rowData.jobType = tryParseIfNotNull(propInfo);

            //Employee Responsible Name
            rowData.employee = excelData.EMPLOYEERESPONSIBLEPRICING ?? "";

            //Segment
            propInfo.propName = PromotionExpirationTemplateErrorKeys.segment;
            propInfo.value = excelData.SEGMENT;
            rowData.segment = tryParseIfNotNull(propInfo);

            //Tier
            propInfo.propName = PromotionExpirationTemplateErrorKeys.tier;
            propInfo.value = excelData.TIER;
            rowData.tier = tryParseIfNotNull(propInfo);

            //Channel
            propInfo.propName = PromotionExpirationTemplateErrorKeys.channel;
            propInfo.value = excelData.CHANNEL;
            rowData.channel = tryParseIfNotNull(propInfo);

            //Price zone
            propInfo.propName = PromotionExpirationTemplateErrorKeys.priceZone;
            propInfo.value = excelData.PRICEZONE;
            rowData.priceZone = tryParseIfNotNull(propInfo);

            //Buying group
            propInfo.propName = PromotionExpirationTemplateErrorKeys.buyingGroup;
            propInfo.value = excelData.BUYINGGROUP;
            rowData.buyingGroup = tryParseIfNotNull(propInfo);

            //Corporate affiliation
            propInfo.propName = PromotionExpirationTemplateErrorKeys.corporateAffiliation;
            propInfo.value = excelData.CORPORATEAFFILIATION;
            rowData.corporateAffiliation = tryParseIfNotNull(propInfo);
            
            //Minimum code
            propInfo.propName = PromotionExpirationTemplateErrorKeys.minimumCode;
            propInfo.value = excelData.MINIMUMCODE;
            rowData.minimumCode = tryParseIfNotNull(propInfo);
            
            //Maximum code
            propInfo.propName = PromotionExpirationTemplateErrorKeys.maxCode;
            propInfo.value = excelData.MAXCODE;
            rowData.maxCode = tryParseIfNotNull(propInfo);

            if (propInfo.log && propInfo.log.length > 0) {
                excelViewerErrorData.push(...propInfo.log);
            }

            validationModel.push(rowData);
        }
        return validationModel;
    };

    
    //Client validation model => validation API request model
    const convertToRequestModel = (validatedExcel: PromotionExpirationExcelDataRow[]) => {
        const requestTemplate = validatedExcel.map(row => { 
                return { AgreementNumber: `${row.agreementNumber}`,
                         ControlNumber: `${row.controlNumber}`,
                         CustomerId: row.customerId || null,
                         CustomerName: `${row.customerName}`,
                         CustomerAddress: `${row.customerAddress}`,

                         Segment: row.segment || null,
                         SegmentDescription: `${row.segmentDescription}`,
                         Tier: row.tier || null,
                         TierDescription: `${row.tierDescription}`,
                         Channel: row.channel || null,
                         ChannelDescription: `${row.channelDescription}`,
                         BuyingGroup: row.buyingGroup || null,
                         BuyingGroupDescription: `${row.buyingGroupDescription}`,
                         CorporateAffiliation: row.corporateAffiliation?.toString() || null,
                         CorporateAffiliationDescription: `${row.corporateAffiliationDescription}`,
                         CountryCode: `${row.countryCode}`,
                         CountryDescription: `${row.countryDescription}`,
                         PriceZone: row.priceZone || null,
                         PriceZoneDescription: `${row.priceZoneDescription}`,
                         FreightZone: `${row.freightZone}`,
                    
                         ProductLine: row.productLine || null,
                         ProductLineDescription: `${row.productLineDescription}`,
                         ProductLineCategory: row.productLineCategory || null,
                         ProductLineCategoryDescription: `${row.productLineCategoryDescription}`,
                         ItemNumber: row.itemNumber || null,
                         ItemDescription: `${row.itemDescription}`,
                    
                         ManufacturerNumber: `${row.manufacturerNumber}`,
                         Gtin: `${row.gtin}`,

                         SupplierIndicator: row.supplierFlag || "N",
                         JobType: row.jobType || null,
                         JobTypeDescription: `${row.jobTypeDescription}`,
                         EmployeeResponsibleName: row.employee?.toString().trim() || null,

                         PromotionId: `${row.promotionId}`,
                         PromotionIdDescription: `${row.promotionIdDescription}`,
                         PromotionAmount: row.promotionAmount || "0",
                         UnitOfMeasure: `${row.uom}`,
                         PromotionPercentage: row.promotionPercent || "0",

                         MinimumValue: `${row.minimumValue}`,
                         MinimumCode: row.minimumCode || null,
                         MinimumDescription: `${row.minimumDescription}`,

                         MaximumValue: `${row.maximumValue}`,
                         MaximumCode: row.maxCode || null,
                         MaximumDescription: `${row.maximumDescription}`,

                         BeginOrderDate: `${row.beginOrderDate}`,
                         EndOrderDate: `${row.endOrderDate}`,
                         ExtendOrderDate: `${row.extendOrderDate}`,

                         BeginDeliveryDate: `${row.beginDeliveryDate}`,
                         EndDeliveryDate: `${row.endDeliveryDate}`,
                         ExtendDeliveryDate: `${row.extendDeliveryDate}`,

                         ContactEmail: `${row.contactEmail}`,
                         Action: `${row.action}`,

                         NewPromotionAmount: `${row.newPromotionAmount}`,
                         NewPromotionUOM: `${row.newPromotionUOM}`,
                         NewBeginningOrderDate: `${row.newBeginningOrderDate}`,
                         NewEndingOrderDate: `${row.newEndingOrderDate}`,
                         NewBeginningDeliveryDate: `${row.newBeginningDeliveryDate}`,
                         NewEndingDeliveryDate: `${row.newEndingDeliveryDate}`,
                    
                         Comments: `${row.comments}` } as PromotionExpirationApiValidationRequestData;
        });

        const requestModel: PromotionExpirationExcelApiValidationRequest = 
        {
            TemplateFileName: fileName,
            CosmosGUID: id,
            SubmitterEmail: getUserEmail(),
            ChangeNotes: changeNotes,
            PromotionExpirationTemplate: requestTemplate
        };

        return requestModel;
    };

    
    //Exports
    const testingExports = { 
        doDateValidation,
        convertToValidationModel,
        convertToRequestModel 
    };

    return { 
        doClientSideValidation, 
        postToValidationApi,
        standardizeExcelPropertyNameFormat,
        testingExports 
    };
}
